import { type ChangeEvent, type InputHTMLAttributes, type Ref, useEffect, useState } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { useDisconnectedState } from "@virtual-brain-lib/frontend-design-system/components";
import {
	cn,
	useDebounce,
	type VariantSlotsToClassNames,
} from "@virtual-brain-lib/frontend-design-system/utils";

import { Button, type ButtonProps } from "./Button";
import { Icon, type IconProps, type Icons } from "./Icon";
import { Span } from "./Span";

export const inputVariants = tv({
	slots: {
		input: "h-full w-full border font-body leading-0 focus-within:outline-1",
		wrapper: "h-full w-full",
		inputWrapper: "group/input-wrapper relative",
		leftIcon: "absolute",
		invalidText: "text-danger",
		clearButton:
			"py-auto h-full not-disabled:hover:bg-transparent not-disabled:active:bg-transparent",
	},
	variants: {
		color: {
			primary: {
				input: "border-primary-border bg-primary-light text-on-primary-2 focus-within:outline-primary-active-ring",
			},
			danger: {
				input: "border-danger-border bg-danger-light text-on-danger-2 focus-within:outline-danger-active-ring",
			},
			info: {
				input: "border-info-border bg-info-light text-on-info-2 focus-within:outline-info-active-ring",
			},
			success: {
				input: "border-success-border bg-success-light text-on-success-2 focus-within:outline-success-active-ring",
			},
			warning: {
				input: "border-warning-border bg-warning-light text-on-warning-2 focus-within:outline-warning-active-ring",
			},
			simple: {
				input: "bg-background text-default focus-within:outline-primary",
			},
		},
		size: {
			xs: {
				input: "min-h-6 rounded-sm px-1.5 py-1 text-xs",
			},
			sm: {
				input: "min-h-7 rounded-sm px-2 py-1.5 text-xs",
			},
			md: {
				input: "min-h-8 rounded-md px-2.5 py-1.5 text-sm",
			},
			lg: {
				input: "text-md min-h-9 rounded-md px-3 py-1.5",
			},
			xl: {
				input: "min-h-12 rounded-lg px-4 py-2 text-lg",
			},
		},
		leftIcon: {
			true: {
				leftIcon: "py-auto h-full",
			},
		},
		isClearable: {
			true: {},
		},
	},
	compoundVariants: [
		{
			size: "xs",
			leftIcon: true,
			className: {
				input: "pl-5",
				leftIcon: "left-1.5",
			},
		},
		{
			size: "xs",
			isClearable: true,
			className: {
				input: "group-hover/input-wrapper:pr-4",
			},
		},
		{
			size: "sm",
			leftIcon: true,
			className: {
				input: "pl-6",
				leftIcon: "left-2",
			},
		},
		{
			size: "sm",
			isClearable: true,
			className: {
				input: "group-hover/input-wrapper:pr-4.5",
			},
		},
		{
			size: "md",
			leftIcon: true,
			className: {
				input: "pl-8",
				leftIcon: "left-2.5",
			},
		},
		{
			size: "md",
			isClearable: true,
			className: {
				input: "group-hover/input-wrapper:pr-6",
			},
		},
		{
			size: "lg",
			leftIcon: true,
			className: {
				input: "pl-9",
				leftIcon: "left-3",
			},
		},
		{
			size: "lg",
			isClearable: true,
			className: {
				input: "group-hover/input-wrapper:pr-7",
			},
		},
		{
			size: "xl",
			leftIcon: true,
			className: {
				input: "pl-12",
				leftIcon: "left-4",
			},
		},
		{
			size: "xl",
			isClearable: true,
			className: {
				// input: "group-hover/input-wrapper:pr-8", // we have bug here..
			},
		},
	],
});

export type InputVariant = VariantProps<typeof inputVariants>;

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange"> &
	Omit<InputVariant, "leftIcon"> & {
		readonly ref?: Ref<HTMLInputElement>;
		readonly classNames?: VariantSlotsToClassNames<typeof inputVariants>;
		readonly leftIcon?: Icons;
		readonly onDebounced?: (value: string) => void;
		readonly onValueChange?: (value: string) => void;
		readonly debounceDelay?: number;
		readonly isInvalid?: boolean | ((value: string) => boolean);
		readonly invalidText?: string;
		readonly isClearable?: boolean;
		readonly onKeyEnter?: () => void;
	};

const iconSize: Record<NonNullable<InputProps["size"]>, NonNullable<IconProps["size"]>> = {
	xs: "xs",
	sm: "xs",
	md: "sm",
	lg: "md",
	xl: "lg",
};
const clearButtonSize: Record<NonNullable<InputProps["size"]>, NonNullable<ButtonProps["size"]>> = {
	xs: "xs",
	sm: "xs",
	md: "sm",
	lg: "md",
	xl: "lg",
};

export function Input({
	ref,
	size = "md",
	color = "simple",
	leftIcon,
	className,
	classNames,
	defaultValue,
	value: propsValue,
	onDebounced,
	debounceDelay = 500,
	isInvalid: isInvalidProps,
	onValueChange,
	invalidText,
	isClearable: propsIsClearable = true,
	onKeyEnter,
	onKeyDown,
	disabled: propsDisabled = false,
	...props
}: InputProps) {
	const { isDisconnected } = useDisconnectedState();
	const disabled = isDisconnected || propsDisabled;
	const [value, setValue] = useState(defaultValue ?? propsValue ?? "");

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		onValueChange?.(e.currentTarget.value);
	};

	useDebounce(value.toString(), debounceDelay, (debouncedValue) => {
		onDebounced?.(debouncedValue);
	});

	useEffect(() => {
		if (propsValue !== value) {
			setValue(propsValue ?? "");
		}
	}, [propsValue]);

	const isInvalid =
		typeof isInvalidProps === "function" ? isInvalidProps(value.toString()) : isInvalidProps;

	const isClearable = value !== "" && propsIsClearable;

	const {
		wrapper,
		inputWrapper,
		input: inputClassName,
		leftIcon: leftIconClassName,
		invalidText: invalidTextClassName,
		clearButton,
	} = inputVariants({
		size,
		color: isInvalid ? "danger" : color,
		leftIcon: Boolean(leftIcon),
		isClearable,
	});

	const handleClear = () => {
		setValue("");
		onValueChange?.("");
	};

	return (
		<div className={wrapper({ className: cn(className, classNames?.wrapper) })}>
			<div className={inputWrapper({ className: classNames?.inputWrapper })}>
				{leftIcon && (
					<Icon
						className={leftIconClassName({ className: classNames?.leftIcon })}
						name={leftIcon}
						color={color === "simple" ? "primary" : color}
						size={iconSize[size]}
					/>
				)}
				<input
					{...props}
					ref={ref}
					disabled={disabled}
					defaultValue={defaultValue}
					value={value}
					className={inputClassName({ className: classNames?.input })}
					onChange={handleChange}
					onKeyDown={(e) => {
						if (onKeyEnter && e.key === "Enter") {
							onKeyEnter();
						}

						onKeyDown?.(e);
					}}
				/>
				{isClearable && (
					<div className="py-auto absolute top-0 right-0 hidden h-full group-hover/input-wrapper:block">
						<Button
							color="danger"
							variant="ghost"
							leftIcon="x"
							className={clearButton({ className: classNames?.clearButton })}
							size={clearButtonSize[size]}
							onClick={handleClear}
						/>
					</div>
				)}
			</div>

			{isInvalid && invalidText && (
				<Span className={invalidTextClassName({ className: classNames?.invalidText })}>
					{invalidText}
				</Span>
			)}
		</div>
	);
}
